import type { CookieService } from "@packages/common";
import { COOKIE_SERVICE, StringFormat } from "@packages/common";
import type { Config } from "@packages/config";
import { CONFIG } from "@packages/config";
import { Inject, Injectable } from "@webmotors/ioc";
import { GEOLOCATION_COOKIE_SERVICE } from "./geolocation.types";
import { GeolocationCookieData, GeolocationData } from "./dtos";

/**
 * Service responsible for handling geolocation data from cookies.
 */
@Injectable(GEOLOCATION_COOKIE_SERVICE)
export class GeolocationCookieService {
  /**
   * Constructs the GeolocationCookieService with the necessary dependencies.
   * @param cookie - The CookieService instance for managing cookies.
   * @param config - The application configuration instance.
   */
  constructor(
    @Inject(COOKIE_SERVICE) private readonly cookie: CookieService,
    @Inject(CONFIG) private readonly config: Config
  ) {}

  /**
   * Retrieves geolocation data from the cookie.
   * @returns An object containing the geolocation data parsed from the cookie.
   * If the cookie is invalid or missing, default values are returned.
   */
  get(): GeolocationData {
    const defaultValue: GeolocationData = {
      state: null,
      city: null,
      location: {
        latitude: null,
        longitude: null,
        radius: null,
      },
    };

    const geolocationCookie = this.cookie.get<GeolocationCookieData | null>(
      this.config.geolocationCookieName
    );

    if (!geolocationCookie) {
      return defaultValue;
    }

    try {
      const { estadocidade, localizacao = {} } = geolocationCookie;

      if (typeof estadocidade !== "string") {
        return defaultValue;
      }

      const [state, city] = (estadocidade?.split(" - ") ?? []).map((value) =>
        value ? StringFormat.formatSlug(value) : null
      );

      return {
        state,
        city,
        location: {
          latitude: localizacao?.latitude ?? null,
          longitude: localizacao?.longitude ?? null,
          radius: localizacao?.raio ?? null,
        },
      };
    } catch {
      return defaultValue;
    }
  }
}
